import { useEffect, useRef, useState } from 'react'
import { isFractionalUnit, normalizeQuantityByUnit } from '@crm/shared'

interface QuantityCellProps {
  value: number
  unit: string | null | undefined
  onChange: (v: number) => void
  onRegister: (el: HTMLInputElement | null) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  cellClass: string
}

// Поле «Кол-во» в таблице позиций. Внутреннее строковое состояние, чтобы
// пользователь мог полностью очистить ячейку и набрать новое число (без
// автоматической подстановки «1», из-за которой ввод «2» превращался в «12»).
// На blur пустое значение коммитится как 0.
//
// Дробные значения разрешены ТОЛЬКО для метрических единиц (л/кг/гр/м).
// Для штучных (шт/pcs/пусто) ввод обрезается до целого — иначе менеджер
// случайно сохранит «1.5 шт. чипа». Когда unit метрический, step=0.001 и
// округление до 3 знаков; иначе step=1 и Math.round.
export function QuantityCell({
  value,
  unit,
  onChange,
  onRegister,
  onKeyDown,
  cellClass,
}: QuantityCellProps) {
  const [local, setLocal] = useState<string>(String(value))
  const focused = useRef(false)
  const fractional = isFractionalUnit(unit)

  useEffect(() => {
    if (!focused.current) setLocal(String(value))
  }, [value])

  const parse = (v: string): number | null => {
    const norm = v.replace(',', '.').trim()
    if (norm === '') return null
    const n = parseFloat(norm)
    if (!Number.isFinite(n)) return null
    return normalizeQuantityByUnit(Math.max(0, n), unit)
  }

  return (
    <input
      type="number"
      ref={onRegister}
      value={local}
      min={0}
      step={fractional ? '0.001' : '1'}
      title={
        fractional
          ? 'Дробные значения разрешены для этой единицы измерения'
          : 'Штучная единица — только целое число'
      }
      onFocus={() => {
        focused.current = true
      }}
      onChange={(e) => {
        const v = e.target.value
        setLocal(v)
        const n = parse(v)
        if (n != null) onChange(n)
      }}
      onBlur={() => {
        focused.current = false
        const n = parse(local)
        if (n == null) {
          setLocal('0')
          onChange(0)
        } else {
          // Если ввели «1.5 шт», нормализация округлит до 2 — синхронизируем
          // отображение с реальным сохранённым значением, чтобы пользователь
          // увидел что произошло.
          setLocal(String(n))
        }
      }}
      onKeyDown={onKeyDown}
      className={`${cellClass} text-right`}
    />
  )
}
